import React from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from '@lingui/core/macro';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useProject } from '../contexts/ProjectContext';
import FontManagement, { FontFamilyInfo } from '../components/FontManagement';
import LogoManagement, { LogoInfo } from '../components/LogoManagement';
import { VideoSettingsPanel, VideoSettings } from '../components/VideoSettings';
import CardFramingPanel from '../components/CardFramingPanel';
import { CollapsibleSection } from '../components/CollapsibleSection';
import NavBar from '../components/NavBar';
import type { FontMetadata } from '../utils/fontHelpers';
import './ProjectSettings.css';

interface ProjectSettingsProps {
  fonts: FontFamilyInfo[];
  logos: LogoInfo[];
  videoSettings: VideoSettings;
  onFontListChange: (fonts: FontFamilyInfo[]) => void;
  onLogoListChange: (logos: LogoInfo[]) => void;
  onVideoSettingsChange: (settings: VideoSettings) => void;
  onFontUploaded?: (fontName: string, fontMetadata: FontMetadata) => void;
  onLogoUploaded?: (logoName: string, serverUrl: string) => void;
  onFontDeleted?: (fontName: string) => void;
  onLogoDeleted?: (logoName: string) => void;
}

const ProjectSettings: React.FC<ProjectSettingsProps> = ({
  fonts,
  logos,
  videoSettings,
  onFontListChange,
  onLogoListChange,
  onVideoSettingsChange,
  onFontUploaded,
  onLogoUploaded,
  onFontDeleted,
  onLogoDeleted,
}) => {
  const { selectedProject } = useProject();
  const navigate = useNavigate();

  if (!selectedProject) {
    return null;
  }

  return (
    <div className="project-settings-page">
      <NavBar />
      <div className="project-settings-content">
        <button
          onClick={() => navigate('/app')}
          className="fancy-button"
          style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}
        >
          <ArrowBackIcon style={{ fontSize: '18px' }} />
          {t`Back to Editor`}
        </button>

        <div className="settings-sections">
          <div className="settings-primary-column">
            <div className="video-settings-section">
              <CollapsibleSection title={String(t`Video Settings`)} contentClassName="video-settings-section-body">
                <VideoSettingsPanel
                  settings={videoSettings}
                  onSettingsChange={onVideoSettingsChange}
                  showTitle={false}
                  showBackgroundControls={videoSettings.mode !== 'cards'}
                />
              </CollapsibleSection>
            </div>
            {videoSettings.mode === 'cards' && (
              <div className="video-settings-section">
                <CollapsibleSection title={String(t`Card Framing`)} contentClassName="video-settings-section-body">
                  <CardFramingPanel
                    settings={videoSettings}
                    onSettingsChange={onVideoSettingsChange}
                  />
                </CollapsibleSection>
              </div>
            )}
          </div>
          <div className="settings-management-column">
            <FontManagement
              onFontListChange={onFontListChange}
              initialFonts={fonts}
              onFontUploaded={onFontUploaded}
              onFontDeleted={onFontDeleted}
            />

            <LogoManagement
              onLogoListChange={onLogoListChange}
              initialLogos={logos}
              onLogoUploaded={onLogoUploaded}
              onLogoDeleted={onLogoDeleted}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectSettings;
